import * as React from "react";
import {
    Button,
    Input,
    Spinner,
    Text,
    makeStyles,
    tokens,
} from "@fluentui/react-components";
import {
    Search24Regular,
    Person24Regular,
    ChevronRight20Regular,
} from "@fluentui/react-icons";
import { apiUrl } from "../utils/api";

const useStyles = makeStyles({
    container: {
        marginTop: "8px",
    },
    label: {
        display: "block",
        marginBottom: "4px",
        fontWeight: 600,
    },
    searchBox: {
        display: "flex",
        gap: "8px",
    },
    resultsContainer: {
        maxHeight: "200px",
        overflowY: "auto",
        marginTop: "8px",
        backgroundColor: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusMedium,
        boxShadow: tokens.shadow4,
        zIndex: 100,
    },
    resultItem: {
        padding: "8px 12px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        "&:hover": {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
        "&:last-child": {
            borderBottom: "none",
        },
    },
    selectedInfo: {
        marginTop: "4px",
        fontSize: "12px",
        color: tokens.colorNeutralForeground2,
        fontStyle: "italic",
    },
});

interface ContactSelectorProps {
    label: string;
    placeholder?: string;
    onSelect: (contact: any) => void;
    selectedContact?: any;
    required?: boolean;
}

export const ContactSelector: React.FC<ContactSelectorProps> = ({
    label,
    placeholder = "Kontakt suchen...",
    onSelect,
    selectedContact,
    required = false,
}) => {
    const s = useStyles();
    const [searchQuery, setSearchQuery] = React.useState("");
    const [results, setResults] = React.useState<any[]>([]);
    const [isSearching, setIsSearching] = React.useState(false);

    const handleSearch = async () => {
        if (!searchQuery) return;
        setIsSearching(true);
        try {
            const res = await fetch(apiUrl("contact", { search: searchQuery, limit: 50 }));
            const data = await res.json();
            setResults(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Failed to search contacts", err);
            setResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    const handleSelect = (contact: any) => {
        onSelect(contact);
        setResults([]);
        setSearchQuery("");
    };

    return (
        <div className={s.container}>
            <label className={s.label}>
                {label}
                {required && (
                    <span style={{ color: tokens.colorStatusDangerForeground1 }}> *</span>
                )}
            </label>
            <div className={s.searchBox}>
                <Input
                    value={searchQuery}
                    onChange={(_, d) => setSearchQuery(d.value)}
                    placeholder={placeholder}
                    contentAfter={
                        isSearching ? (
                            <Spinner size="tiny" />
                        ) : (
                            <Search24Regular
                                onClick={handleSearch}
                                style={{ cursor: "pointer" }}
                            />
                        )
                    }
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            handleSearch();
                        }
                    }}
                    style={{ flex: 1 }}
                />
                <Button onClick={handleSearch} disabled={isSearching}>
                    Suchen
                </Button>
            </div>

            {results.length > 0 && (
                <div className={s.resultsContainer}>
                    {results.map((result) => (
                        <div
                            key={result.id}
                            className={s.resultItem}
                            onClick={() => handleSelect(result)}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <Person24Regular />
                                <Text>
                                    {result.lastname}, {result.vorname ?? result.firstName}
                                    {result.birthDate && (
                                        <span
                                            style={{
                                                color: tokens.colorNeutralForeground3,
                                                fontSize: "12px",
                                                marginLeft: "8px",
                                            }}
                                        >
                                            ({result.birthDate})
                                        </span>
                                    )}
                                </Text>
                            </div>
                            <ChevronRight20Regular />
                        </div>
                    ))}
                </div>
            )}

            {selectedContact && (
                <div className={s.selectedInfo}>
                    Ausgewählt: {selectedContact.lastname}, {selectedContact.vorname ?? selectedContact.firstName}
                    {selectedContact.birthDate && ` (${selectedContact.birthDate})`}
                </div>
            )}
        </div>
    );
};
